import { FC, PropsWithChildren, useMemo } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { useIsEmbedAutoHeight } from '../hooks/useIsEmbedAutoHeight.ts'
import { useIsEmbedMode } from '../hooks/useIsEmbedMode.ts'
import { createTheme } from '../theme/theme.ts'

const CssVariables = styled.div.attrs({ className: 'nectary-theme-base' })<{
  $isEmbedMode: boolean
  $isEmbedAutoHeight: boolean
}>`
  // In embed mode the iframe's box is a fixed height set by the host page
  // (or the module's min-height fallback), and everything below needs a
  // definite height to percentage-size against so overflow can scroll
  // instead of silently spilling past the iframe's own viewport. Without
  // this, this wrapper's default height:auto breaks the height:100% chain
  // the very first link down from #sinch-widget-root. Non-embed mode relies
  // on the opposite: content-driven auto height, reported back to the host
  // via ResizeObserver + postMessage, so this must stay out of that path.
  //
  // $isEmbedAutoHeight is the same "content-driven" case, but for embed mode:
  // the host gave the target element no height at all, so there's nothing
  // to percentage-size against anyway — forcing height:100% here would just
  // chain down to the same fixed min-height fallback. Left at the default
  // height:auto, this and everything below it sizes to its own content
  // instead, which EmbedWidgetUI.tsx measures and reports back.
  ${({ $isEmbedMode, $isEmbedAutoHeight }) => $isEmbedMode && !$isEmbedAutoHeight && 'height: 100%;'}

  // checkbox
  --sinch-comp-checkbox-color-checked-background-initial: ${props => props.theme.palette.default.background};
  --sinch-comp-checkbox-color-checked-background-hover: ${props => props.theme.palette.hover.background};
  --sinch-comp-checkbox-color-checked-background-active: ${props => props.theme.palette.active.background};
  --sinch-comp-checkbox-color-checked-border-initial: ${props => props.theme.palette.default.background};
  --sinch-comp-checkbox-color-checked-border-hover: ${props => props.theme.palette.hover.background};
  --sinch-comp-checkbox-color-checked-border-active: ${props => props.theme.palette.active.background};
  // button primary
  --sinch-comp-button-color-primary-default-background-initial: ${props => props.theme.palette.default.background};
  --sinch-comp-button-color-primary-default-background-hover: ${props => props.theme.palette.hover.background};
  --sinch-comp-button-color-primary-default-background-active: ${props => props.theme.palette.active.background};
  --sinch-comp-button-color-primary-default-icon-initial: ${({ theme }) => theme.buttonDarkIcon};
  --sinch-comp-button-color-primary-default-text-initial: ${props => props.theme.palette.default.text};
  --sinch-comp-button-color-primary-disabled-background-initial: ${props => props.theme.palette.disabled.background};
  --sinch-comp-button-color-primary-disabled-text-initial: ${props => props.theme.palette.disabled.text};
  // links
  --sinch-comp-link-color-default-text-initial: var(--sinch-global-color-text);
  --sinch-comp-link-color-default-text-hover: ${props => props.theme.palette.default.background};
`

type ThemeProviderProps = {
  primaryColor?: string
}

export const WidgetThemeProvider: FC<PropsWithChildren<ThemeProviderProps>> = ({ primaryColor, children }) => {
  const theme = useMemo(() => createTheme(primaryColor), [primaryColor])
  const isEmbedMode = useIsEmbedMode()
  const isEmbedAutoHeight = useIsEmbedAutoHeight()
  return (
    <ThemeProvider theme={theme}>
      <CssVariables $isEmbedMode={isEmbedMode} $isEmbedAutoHeight={isEmbedAutoHeight}>
        {children}
      </CssVariables>
    </ThemeProvider>
  )
}
